import { Injectable } from "@angular/core";
import * as Iso3166Module from "iso-3166-2";

export interface StateSubdivisionOption {
  code: string; // BR-SP
  shortCode: string; // SP
  name: string; // São Paulo
}

interface IsoSubdivision {
  type: string;
  name: string;
}

interface IsoCountry {
  name: string;
  sub?: Record<string, IsoSubdivision>;
}

interface Iso3166Api {
  country: (code: string) => IsoCountry | undefined;
}

@Injectable({ providedIn: "root" })
export class CountriesService {
  getSubdivisions(countryCode: string): StateSubdivisionOption[] {
    const iso = this.resolveIso3166();
    const normalized = countryCode?.trim().toUpperCase();
    if (!iso || !normalized) {
      return [];
    }

    try {
      const country = iso.country(normalized);
      const subs = country?.sub;
      if (!subs) {
        return [];
      }

      return Object.entries(subs)
        .map(([fullCode, sub]) => {
          const parts = fullCode.split("-");
          const shortCode = parts.length > 1 ? parts.slice(1).join("-") : fullCode;
          return {
            code: fullCode,
            shortCode,
            name: sub?.name || shortCode || fullCode,
          };
        })
        .filter((option) => option.name)
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    } catch {
      return [];
    }
  }

  toFormValue(option: StateSubdivisionOption, countryCode: string): string {
    if (countryCode?.toUpperCase() === "BR") {
      return option.shortCode;
    }

    if (/^[A-Za-z]{1,6}$/.test(option.shortCode)) {
      return option.shortCode;
    }

    return option.name;
  }

  matchSubdivision(
    countryCode: string,
    longName?: string | null,
    shortName?: string | null,
  ): StateSubdivisionOption | null {
    const options = this.getSubdivisions(countryCode);
    if (!options.length) {
      return null;
    }

    const longN = this.normalize(longName);
    const shortN = this.normalize(shortName);

    return (
      options.find((option) => {
        const nameN = this.normalize(option.name);
        const codeN = this.normalize(option.shortCode);
        const fullN = this.normalize(option.code);

        return (
          (longN &&
            (nameN === longN || nameN.includes(longN) || longN.includes(nameN))) ||
          (shortN &&
            (codeN === shortN ||
              nameN === shortN ||
              fullN === shortN ||
              fullN.endsWith(`-${shortN}`)))
        );
      }) ?? null
    );
  }

  private normalize(value?: string | null): string {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .replace(
        /^(departamento|department|province|estado|region|regiao|provincia)\s+/i,
        "",
      )
      .trim()
      .toLowerCase();
  }

  private resolveIso3166(): Iso3166Api | null {
    const mod = Iso3166Module as unknown as Iso3166Api & { default?: Iso3166Api };
    if (typeof mod.country === "function") {
      return mod;
    }
    if (typeof mod.default?.country === "function") {
      return mod.default;
    }
    return null;
  }
}
